import * as PIXI from "pixi.js";

import { TGameAssetAlias } from "@src/game/screens/GameScreen";
import { TInitialResponse } from "@src/server/ResponseTypes";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { gameEngine } from "@src/game/GameEngine";
import { Cell } from "@src/game/cell-panel/Cell";
import { Assets } from "@src/core/assets/Assets";
import { constants } from "@src/game/Constants";
import gsap from "gsap";

export class CellPanel implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _cells!: Cell[][];
  public get cells(): Cell[][] {
    return this._cells;
  }
  private _specialCells!: Cell[];
  private _panelHoldAndWin!: PIXI.Sprite;

  private _animationTween!: GSAPTween;

  private _showPanelTargetY: number = 0;
  // The panel should be hidden by default and ready to fire its play animation.
  private _hasRunHideAnimation: boolean = true;
  private _hasHiddenEventFired: boolean = false;

  public constructor() {
    this._cells = [];
    this._specialCells = [];

    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();
  }

  // Smoothly brings the panel back to its regular position
  public playShowAnimation(): void {
    if (!this._hasRunHideAnimation) {
      return;
    }

    this._view.visible = true;

    this._animationTween?.kill();
    this._hasHiddenEventFired = false;
    this._view.y = 1600;

    this._animationTween = gsap.to(this._view, {
      // TODO: Use the actual target value.
      y: this._showPanelTargetY,
      duration: 0.4,
      ease: "back.out(1)",
      onStart: () => {
        EventSystem.invoke(
          constants.GameEvents.CELL_PANEL_SHOW_ANIMATION_STARTED,
        );
        console.log("CELL PANEL SHOW ANIMATION STARTED");
      },
      onComplete: () => {
        this._handleShowAnimationCompletion();
      },
      onInterrupt: () => {
        this._handleShowAnimationCompletion();
      },
    });
  }

  // Smoothly lowers the panel to below-screen
  public playHideAnimation(): void {
    this._hasRunHideAnimation = true;

    this._animationTween?.kill();

    // this._symbolReels.forEach((reel) => {
    //   reel.view.mask = null;
    // });

    this._animationTween = gsap.to(this._view, {
      y: 1600,
      duration: 0.4,
      ease: "back.in(1)",
      onComplete: () => {
        this._handleHideAnimationCompletion();
      },
      onInterrupt: () => {
        this._handleHideAnimationCompletion();
      },
    });
  }

  private _handleShowAnimationCompletion(): void {
    this._hasRunHideAnimation = false;
    console.log("CELL PANEL SHOWN");

    EventSystem.invoke(constants.GameEvents.CELL_PANEL_SHOWN);
    // this._symbolReels.forEach((reel) => {
    //   reel.view.mask = this._symbolPanelMaskGraphics;
    // });
  }

  private _handleHideAnimationCompletion(): void {
    if (!this._hasHiddenEventFired) {
      console.log("CELL PANEL HIDDEN");

      this._view.visible = false;

      this._hasHiddenEventFired = true;
      EventSystem.invoke(constants.GameEvents.CELL_PANEL_HIDDEN);
    }
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.GAME_INITIALIZE,
      this._onGameInitialized.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequest.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequest.bind(this),
    );
  }

  private _onGameInitialized(_: TInitialResponse): void {
    this._createCells();
    this._createSpecialCells();
  }

  private _onLandscapeLayoutRequest(): void {
    // This value is based on GameScreen's positioning of CellPanel.
    this._showPanelTargetY = -50;
  }

  private _onPortraitLayoutRequest(): void {
    // This value is based on GameScreen's positioning of CellPanel.
    this._showPanelTargetY = -300;
  }

  private _initializeAssets(): void {
    const panelSprite = Assets.getAsset<TGameAssetAlias>("cellPanelImg");

    this._panelHoldAndWin = new PIXI.Sprite(panelSprite);
    this._panelHoldAndWin.position.set(0.6, 1);
    this._panelHoldAndWin.anchor.set(0.5);
    this._panelHoldAndWin.scale.set(1);
    // this._panelHoldAndWin.position.set(-2, 0);
    this._panelHoldAndWin.visible = true;
    this._panelHoldAndWin.zIndex = 2;
  }

  private _parentAssets(): void {
    this._view.addChild(this._panelHoldAndWin);
  }

  private _createCells(): void {
    for (let i = 0; i < gameEngine.config.reels; i++) {
      this._cells[i] = [];

      for (let j = 0; j < gameEngine.config.rows; j++) {
        const cell: Cell = new Cell("symbol", i, j);

        const cellX: number =
          i * Cell.WIDTH -
          (gameEngine.config.reels * Cell.WIDTH) / 2 +
          Cell.WIDTH / 2;
        const cellY: number = Cell.getCenteredCellPositionY(j);

        cell.view.x = cellX;
        cell.view.y = cellY;
        cell.cellMaskView.x = cellX;
        cell.cellMaskView.y = cellY;
        cell.view.zIndex = 1;

        this._view.addChild(cell.view);
        this._view.addChild(cell.cellMaskView);

        this._cells[i].push(cell);
      }
    }
  }

  private _createSpecialCells(): void {
    for (let i = 0; i < gameEngine.config.reels; i++) {
      const cell: Cell = new Cell("special", i, -1);

      const cellX: number =
        i * Cell.WIDTH -
        (gameEngine.config.reels * Cell.WIDTH) / 2 +
        Cell.WIDTH / 2;
      const cellY: number = Cell.getCenteredCellPositionY(-1);

      cell.view.x = cellX;
      cell.view.y = cellY;
      cell.cellMaskView.x = cellX;
      cell.cellMaskView.y = cellY;
      cell.view.zIndex = 1;

      cell.view.scale.set(1.05);

      this._view.addChild(cell.view);
      this._view.addChild(cell.cellMaskView);

      this._specialCells[i] = cell;
    }
  }
}
